import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Button, Tooltip, IconButton, InputGroup, Input, InputLeftElement, Text, Spinner,
  Center, Flex, Select, Collapse, Editable, EditablePreview, EditableInput, useColorModeValue,
  Checkbox, HStack, Portal,
  useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton
} from "@chakra-ui/react";
import { AddIcon, ChevronRightIcon, EditIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { BiMessageAlt, BiSearch, BiFolder, BiFolderOpen, BiTrash, BiFolderPlus } from "react-icons/bi";
import { BsLayoutSidebarInset } from "react-icons/bs";
import { FaArrowRight } from "react-icons/fa";
import { auth } from '../firebase';
import { FolderInterface } from '../configs';

interface Chat {
  title: string;
  folderId?: string;
  status?: string;
  active?: boolean;
}

interface SidebarProps {
  isDesktop: boolean | undefined;
  showBar: boolean;
  setShowBar: (showBar: boolean) => void;
  allChats: Record<string, Chat>;
  currentChat: string;
  setCurrentChat: (chatId: string) => void;
  startNewDialogue: () => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  matchedChats: string[];
  loadingSearch: boolean;
  setApiAddress: (apiAddress: string) => void;
  apiAddress: string;
  navigate: (path: string) => void;
  loadMoreChats: () => void;
  hasMoreChats: boolean;
  loadingMoreChats: boolean;
  folders?: Record<string, FolderInterface>;
  createFolder?: () => Promise<string>;
  moveChatToFolder?: (chatId: string, folderId: string) => void;
  removeChatFromFolder?: (chatId: string) => void;
  updateFolderTitle?: (folderId: string, title: string) => void;
  deleteFolder?: (folderId: string) => void;
  onBulkDelete?: (chatIds: string[]) => void;
  onDeleteAllChats?: () => Promise<void>;
}

const MENU_MARGIN = 12;
const MENU_MIN_WIDTH = 220;
const MENU_TARGET_HEIGHT = 360;

function getFolderSortTime(folder: FolderInterface): number {
  return folder.lastChatMovedAt || folder.createdAt || 0;
}

function sortFoldersByRecentMove(folders: FolderInterface[]): FolderInterface[] {
  return folders.sort((a, b) => {
    const timeDiff = getFolderSortTime(b) - getFolderSortTime(a);
    return timeDiff !== 0 ? timeDiff : a.title.localeCompare(b.title);
  });
}

// Context menu for a chat item — "Move to folder" with submenu
const ChatContextMenu: React.FC<{
  chatId: string;
  folders: Record<string, FolderInterface>;
  currentFolderId?: string;
  onMoveToFolder: (chatId: string, folderId: string) => void;
  onRemoveFromFolder: (chatId: string) => void;
  onCreateFolderAndMove: (chatId: string) => void;
  children: React.ReactElement;
}> = ({ chatId, folders, currentFolderId, onMoveToFolder, onRemoveFromFolder, onCreateFolderAndMove, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [folderSearchText, setFolderSearchText] = useState('');

  const menuBg = useColorModeValue("gray.800", "gray.700");
  const menuBorder = useColorModeValue("gray.600", "gray.600");
  const menuItemHover = useColorModeValue("gray.700", "gray.600");
  const menuItemColor = useColorModeValue("gray.100", "gray.200");
  const menuDividerColor = useColorModeValue("gray.600", "gray.500");

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
    setFolderSearchText('');
    onOpen();
  }, [onOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = () => onClose();
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const sortedFolders = useMemo(() =>
    sortFoldersByRecentMove(Object.values(folders)),
    [folders]
  );

  // Get available folders (exclude current folder)
  const availableFolders = sortedFolders.filter(f => f.id !== currentFolderId);
  const visibleFolders = useMemo(() => {
    const normalizedSearchText = folderSearchText.trim().toLowerCase();
    if (!normalizedSearchText) return availableFolders;

    return availableFolders.filter(folder =>
      folder.title.toLowerCase().includes(normalizedSearchText)
    );
  }, [availableFolders, folderSearchText]);
  const menuPosition = useMemo(() => {
    if (typeof window === 'undefined') return menuPos;

    return {
      x: Math.max(
        MENU_MARGIN,
        Math.min(menuPos.x, window.innerWidth - MENU_MIN_WIDTH - MENU_MARGIN)
      ),
      y: Math.max(
        MENU_MARGIN,
        Math.min(menuPos.y, window.innerHeight - MENU_TARGET_HEIGHT - MENU_MARGIN)
      )
    };
  }, [menuPos]);

  return (
    <>
      <Box onContextMenu={handleContextMenu} cursor="context-menu">
        {children}
      </Box>
      {isOpen && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            left={`${menuPosition.x}px`}
            top={`${menuPosition.y}px`}
            zIndex={9999}
            bg={menuBg}
            border="1px solid"
            borderColor={menuBorder}
            borderRadius="lg"
            boxShadow="xl"
            py={1.5}
            minW={`${MENU_MIN_WIDTH}px`}
            maxW={`calc(100vw - ${menuPosition.x + MENU_MARGIN}px)`}
            maxH={`calc(100vh - ${menuPosition.y + MENU_MARGIN}px)`}
            display="flex"
            flexDirection="column"
            onClick={(e) => e.stopPropagation()}
          >
            {availableFolders.length > 0 && (
              <>
                <Text px={3} py={1} fontSize="11px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                  Move to folder
                </Text>
                <Box px={2} py={1}>
                  <InputGroup size="sm">
                    <InputLeftElement h="100%" w="2rem" pointerEvents="none">
                      <BiSearch color="var(--chakra-colors-gray-500)" size="14px" />
                    </InputLeftElement>
                    <Input
                      value={folderSearchText}
                      onChange={(e) => setFolderSearchText(e.target.value)}
                      placeholder="Search folders"
                      color={menuItemColor}
                      bg="whiteAlpha.100"
                      borderColor="transparent"
                      _placeholder={{ color: "gray.500" }}
                      _hover={{ bg: "whiteAlpha.200", borderColor: "transparent" }}
                      _focus={{ bg: "whiteAlpha.200", borderColor: "gray.500", boxShadow: "none" }}
                      fontSize="13px"
                      h="28px"
                      pl="2rem"
                      borderRadius="md"
                      autoFocus
                    />
                  </InputGroup>
                </Box>
                <Box overflowY="auto" overscrollBehavior="contain" flex="1" minH={0}>
                  {visibleFolders.length === 0 && (
                    <Text px={3} py={2} fontSize="13px" color="gray.400">
                      No folders found
                    </Text>
                  )}
                  {visibleFolders.map(folder => (
                    <Box
                      key={folder.id}
                      px={3}
                      py={1.5}
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      color={menuItemColor}
                      fontSize="13px"
                      _hover={{ bg: menuItemHover }}
                      borderRadius="md"
                      mx={1}
                      onClick={() => { onMoveToFolder(chatId, folder.id); onClose(); }}
                    >
                      <BiFolder size="14px" />
                      <Text noOfLines={1} flex={1} minW={0}>{folder.title}</Text>
                      <FaArrowRight size="10px" opacity={0.5} />
                    </Box>
                  ))}
                </Box>
                <Box h="1px" bg={menuDividerColor} my={1.5} mx={2} />
              </>
            )}
            {currentFolderId && (
              <Box
                px={3}
                py={1.5}
                cursor="pointer"
                display="flex"
                alignItems="center"
                gap={2}
                color={menuItemColor}
                fontSize="13px"
                _hover={{ bg: menuItemHover }}
                borderRadius="md"
                mx={1}
                onClick={() => { onRemoveFromFolder(chatId); onClose(); }}
              >
                <BiTrash size="14px" />
                <Text>Remove from folder</Text>
              </Box>
            )}
            <Box
              px={3}
              py={1.5}
              cursor="pointer"
              display="flex"
              alignItems="center"
              gap={2}
              color={menuItemColor}
              fontSize="13px"
              _hover={{ bg: menuItemHover }}
              borderRadius="md"
              mx={1}
              onClick={() => { onCreateFolderAndMove(chatId); onClose(); }}
            >
              <BiFolderPlus size="14px" />
              <Text>New folder</Text>
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
};

// Chat button — simplified, no DnD
const ChatButton: React.FC<{
  chatId: string;
  title: string;
  isCurrentChat: boolean;
  onClick: () => void;
  isFolderItem?: boolean;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (chatId: string) => void;
}> = React.memo(({ chatId, title, isCurrentChat, onClick, isFolderItem, isSelectionMode, isSelected, onToggleSelection }) => {
  const currentChatBg = useColorModeValue("#addbed", "blue.700");
  const currentChatColor = useColorModeValue("black", "white");
  const chatButtonHoverBg = useColorModeValue("rgba(255, 255, 255, 0.25)", "rgba(255,255,255,0.1)");
  const chatButtonColor = useColorModeValue("#E0E0E0", "gray.200");
  const checkboxBorderColor = useColorModeValue("gray.400", "gray.500");

  return (
    <Tooltip label={title} isDisabled={isSelectionMode} placement='right'>
      <HStack w={isFolderItem ? "calc(100% - 16px)" : "100%"} ml={isFolderItem ? 4 : 0} mb="2" spacing={2}>
        {isSelectionMode && (
          <Checkbox
            isChecked={isSelected}
            onChange={() => onToggleSelection && onToggleSelection(chatId)}
            colorScheme="blue"
            borderColor={checkboxBorderColor}
            ml={4}
          />
        )}
        <Button
          id={`ChatSOC - Revisiting Chat ${chatId}`}
          size="sm"
          onClick={onClick}
          mb="0"
          borderRadius="md"
          bg={isCurrentChat ? currentChatBg : "transparent"}
          color={isCurrentChat ? currentChatColor : chatButtonColor}
          justifyContent="flex-start"
          variant={isCurrentChat ? "solid" : "ghost"}
          w="100%"
          leftIcon={!isSelectionMode ? <BiMessageAlt fontSize="15px" /> : undefined}
          transition="all 0.2s"
          _hover={{
            bg: isCurrentChat ? currentChatBg : chatButtonHoverBg,
          }}
          width="100%"
        >
          <Text
            w="100%"
            textAlign="left"
            style={{ WebkitMaskImage: "linear-gradient(to right, rgba(0,0,0,1) 70%, rgba(0,0,0,0))" }}
            fontSize="15px"
            fontWeight="400"
          >
            {title}
          </Text>
        </Button>
      </HStack>
    </Tooltip>
  );
});

// Folder component — simplified, no DnD
const FolderComponent: React.FC<{
  folder: FolderInterface;
  chats: string[];
  allChats: Record<string, Chat>;
  currentChat: string;
  folders: Record<string, FolderInterface>;
  onChatClick: (chatId: string) => () => void;
  onUpdateTitle: (folderId: string, title: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveToFolder: (chatId: string, folderId: string) => void;
  onRemoveFromFolder: (chatId: string) => void;
  onCreateFolderAndMove: (chatId: string) => void;
  isSelectionMode?: boolean;
  selectedChats?: Set<string>;
  onToggleSelection?: (chatId: string) => void;
}> = ({ folder, chats, allChats, currentChat, folders, onChatClick, onUpdateTitle, onDeleteFolder, onMoveToFolder, onRemoveFromFolder, onCreateFolderAndMove, isSelectionMode, selectedChats, onToggleSelection }) => {
  const [isOpen, setIsOpen] = useState(false);

  const folderHoverBg = useColorModeValue("rgba(255,255,255,0.05)", "rgba(255,255,255,0.05)");
  const folderColor = useColorModeValue("gray.300", "gray.300");
  const editableColor = useColorModeValue("white", "white");
  const editableFocusBorder = useColorModeValue("#addbed", "#4FD1C5");
  const collapseLineBg = useColorModeValue("rgba(255,255,255,0.1)", "rgba(255,255,255,0.1)");

  return (
    <Box mb={2}>
      <Flex
        align="center"
        px={2}
        py={1.5}
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        borderRadius="md"
        _hover={{ bg: folderHoverBg }}
        color={folderColor}
      >
        <Box transition="transform 0.2s" transform={isOpen ? "rotate(90deg)" : "rotate(0deg)"}>
          <ChevronRightIcon boxSize={5} />
        </Box>

        <Box ml={2} mr={2}>
          {isOpen ? <BiFolderOpen size="18px" /> : <BiFolder size="18px" />}
        </Box>

        <Box flex={1}>
          <Editable
            defaultValue={folder.title}
            fontSize="15px"
            fontWeight="500"
            color={editableColor}
            isDisabled={!isOpen}
            onSubmit={(newTitle) => onUpdateTitle(folder.id, newTitle)}
            onClick={(e) => isOpen && e.stopPropagation()}
          >
            <EditablePreview width="100%" noOfLines={1} cursor={isOpen ? "text" : "pointer"} />
            <EditableInput _focus={{ boxShadow: "none", borderBottom: "1px solid", borderColor: editableFocusBorder }} />
          </Editable>
        </Box>
        {isOpen && (
          <Tooltip label="Delete folder">
            <IconButton
              aria-label="Delete folder"
              icon={<BiTrash size="16px" />}
              size="xs"
              variant="ghost"
              color={folderColor}
              _hover={{ color: "red.400", bg: "rgba(255,0,0,0.1)" }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteFolder(folder.id);
              }}
            />
          </Tooltip>
        )}
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box
          pl={2}
          position="relative"
          _before={{
            content: '""',
            position: "absolute",
            left: "12px",
            top: "0",
            bottom: "10px",
            width: "1px",
            bg: collapseLineBg
          }}
        >
          {chats.map(chatId => (
            <ChatContextMenu
              key={chatId}
              chatId={chatId}
              folders={folders}
              currentFolderId={folder.id}
              onMoveToFolder={onMoveToFolder}
              onRemoveFromFolder={onRemoveFromFolder}
              onCreateFolderAndMove={onCreateFolderAndMove}
            >
              <Box>
                <ChatButton
                  chatId={chatId}
                  title={allChats[chatId]?.title || "Untitled"}
                  isCurrentChat={currentChat === chatId}
                  onClick={onChatClick(chatId)}
                  isFolderItem
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedChats?.has(chatId)}
                  onToggleSelection={onToggleSelection}
                />
              </Box>
            </ChatContextMenu>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const Sidebar: React.FC<SidebarProps> = React.memo(({
  isDesktop,
  showBar,
  setShowBar,
  allChats,
  currentChat,
  setCurrentChat,
  startNewDialogue,
  searchText,
  setSearchText,
  matchedChats,
  loadingSearch,
  setApiAddress,
  apiAddress,
  navigate,
  loadMoreChats,
  hasMoreChats,
  loadingMoreChats,
  folders,
  createFolder,
  moveChatToFolder,
  removeChatFromFolder,
  updateFolderTitle,
  deleteFolder,
  onBulkDelete,
  onDeleteAllChats
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState<Set<string>>(new Set());

  // Delete All Chats modal state
  const { isOpen: isDeleteAllOpen, onOpen: onDeleteAllOpen, onClose: onDeleteAllClose } = useDisclosure();
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const CONFIRM_PHRASE = 'Yes, I want to delete all my chats';

  // Reset selection when mode changes
  useEffect(() => {
    if (!isSelectionMode) {
      setSelectedChats(new Set());
    }
  }, [isSelectionMode]);

  const toggleSelection = useCallback((chatId: string) => {
    setSelectedChats(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chatId)) {
        newSet.delete(chatId);
      } else {
        newSet.add(chatId);
      }
      return newSet;
    });
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (onBulkDelete && selectedChats.size > 0) {
      if (window.confirm(`Are you sure you want to delete ${selectedChats.size} chats? This cannot be undone.`)) {
        onBulkDelete(Array.from(selectedChats));
        setIsSelectionMode(false);
      }
    }
  }, [onBulkDelete, selectedChats]);

  const handleNewDialogue = useCallback(() => {
    startNewDialogue();
    if (!isDesktop) {
      setShowBar(false);
    }
  }, [startNewDialogue, isDesktop, setShowBar]);

  const handleCloseSidebar = useCallback(() => {
    setShowBar(false);
  }, [setShowBar]);

  const handleApiChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setApiAddress(e.target.value);
  }, [setApiAddress]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, [setSearchText]);

  const createChatClickHandler = useCallback((chatId: string) => () => {
    setCurrentChat(chatId);
    navigate(`/chat?id=${chatId}`);
    if (!isDesktop) {
      setShowBar(false);
    }
  }, [setCurrentChat, navigate, isDesktop, setShowBar]);

  // Context menu handlers
  const handleMoveToFolder = useCallback((chatId: string, folderId: string) => {
    if (moveChatToFolder) moveChatToFolder(chatId, folderId);
  }, [moveChatToFolder]);

  const handleRemoveFromFolder = useCallback((chatId: string) => {
    if (removeChatFromFolder) removeChatFromFolder(chatId);
  }, [removeChatFromFolder]);

  const handleCreateFolderAndMove = useCallback(async (chatId: string) => {
    if (createFolder && moveChatToFolder) {
      const folderId = await createFolder();
      moveChatToFolder(chatId, folderId);
    }
  }, [createFolder, moveChatToFolder]);

  const handleCreateEmptyFolder = useCallback(() => {
    if (createFolder) createFolder();
  }, [createFolder]);

  const handleDeleteAllChats = useCallback(async () => {
    if (!onDeleteAllChats || isDeleting) return;
    setIsDeleting(true);
    try {
      await onDeleteAllChats();
      onDeleteAllClose();
      setDeleteConfirmText('');
    } catch {
      // Error toast is shown by the parent
    } finally {
      setIsDeleting(false);
    }
  }, [onDeleteAllChats, isDeleting, onDeleteAllClose]);

  const handleDeleteAllModalClose = useCallback(() => {
    if (isDeleting) return; // Don't allow closing while deleting
    setDeleteConfirmText('');
    onDeleteAllClose();
  }, [isDeleting, onDeleteAllClose]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    const root = scrollContainerRef.current;
    if (!target || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreChats && hasMoreChats && searchText.length === 0) {
          loadMoreChats();
        }
      },
      { root, rootMargin: "80px 0px", threshold: 0.1 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [loadingMoreChats, hasMoreChats, searchText, loadMoreChats]);

  // Organize chats into folders
  const { orphanChats, folderGroups } = useMemo(() => {
    const chatIds = (searchText.length > 0 ? matchedChats : Object.keys(allChats))
      .filter(chatId => allChats[chatId]?.status !== 'deleted' && allChats[chatId]?.active !== false);
    const orphans: string[] = [];
    const groups: Record<string, string[]> = {};

    if (folders && searchText.length === 0) {
      Object.keys(folders).forEach(fid => {
        groups[fid] = [];
      });
    }

    chatIds.forEach(chatId => {
      const chat = allChats[chatId];
      if (chat && chat.folderId && folders && folders[chat.folderId]) {
        if (!groups[chat.folderId]) groups[chat.folderId] = [];
        groups[chat.folderId].push(chatId);
      } else {
        orphans.push(chatId);
      }
    });

    return { orphanChats: orphans, folderGroups: groups };
  }, [allChats, folders, searchText, matchedChats]);

  const showApiSelection = process.env.REACT_APP_CURRENT_ENV !== 'production' && ['rodrigo', '144'].includes(auth.currentUser?.uid ?? '');
  const showSearch = Object.keys(allChats).length >= 2;

  // Theme colors
  const sidebarBg = useColorModeValue("#17404e", "gray.900");
  const sidebarBorderRight = useColorModeValue("#5b5b5b", "gray.700");
  const newDialogueButtonBg = useColorModeValue("#addbed", "#2b3a4e");
  const newDialogueButtonHoverBg = useColorModeValue("#ffc10e", "#415b7d");
  const newDialogueButtonBorderColor = useColorModeValue("white", "gray.600");
  const iconButtonHoverBg = useColorModeValue("whiteAlpha.200", "whiteAlpha.200");
  const apiSelectText = useColorModeValue("white", "white");
  const apiSelectBg = useColorModeValue("white", "gray.700");
  const searchInputBg = useColorModeValue("white", "gray.700");
  const searchIconColor = useColorModeValue('gray.300', 'gray.400');
  const spinnerColor = useColorModeValue("gray.300", "white");

  // Modal theme colors
  const modalBg = useColorModeValue("white", "gray.800");
  const modalHeaderColor = useColorModeValue("red.600", "red.300");
  const modalTextColor = useColorModeValue("gray.700", "gray.300");
  const modalSecondaryTextColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      h="100%"
      minH={0}
      w={!isDesktop && showBar ? "100%" : "20%"}
      bg={sidebarBg}
      borderRight="1.5px solid"
      borderColor={sidebarBorderRight}
      position="relative"
      fontFamily={'"Elza", sans-serif'}
    >
      <Box ref={scrollContainerRef} h="100%" minH={0} overflowY="auto" overflowX="hidden">
        <Flex>
          <Button
            w="100%"
            id="ChatSOC - New Chat Created"
            bg={newDialogueButtonBg}
            _hover={{ bg: newDialogueButtonHoverBg }}
            borderRadius={0}
            borderColor={newDialogueButtonBorderColor}
            marginRight="2%"
            leftIcon={<AddIcon />}
            onClick={handleNewDialogue}
          >
            New Dialogue
          </Button>
          <Tooltip label="New folder">
            <IconButton
              onClick={handleCreateEmptyFolder}
              variant="ghost"
              color="white"
              aria-label="New folder"
              icon={<BiFolderPlus size="18px" />}
              _hover={{ bg: iconButtonHoverBg }}
            />
          </Tooltip>
          <Tooltip label={isSelectionMode ? "Cancel selection" : "Edit list"}>
            <IconButton
              onClick={() => setIsSelectionMode(!isSelectionMode)}
              variant="ghost"
              color="white"
              aria-label="Edit list"
              icon={isSelectionMode ? <CloseIcon /> : <EditIcon />}
              _hover={{ bg: iconButtonHoverBg }}
            />
          </Tooltip>
          {isSelectionMode && (
            <Tooltip label={`Delete ${selectedChats.size} chats`}>
              <IconButton
                onClick={handleBulkDelete}
                isDisabled={selectedChats.size === 0}
                variant="ghost"
                color="red.300"
                aria-label="Delete selected"
                icon={<DeleteIcon />}
                _hover={{ bg: iconButtonHoverBg, color: "red.200" }}
              />
            </Tooltip>
          )}

          <Tooltip label="Close sidebar">
            <IconButton
              onClick={handleCloseSidebar}
              variant="ghost"
              color="white"
              aria-label="Close sidebar"
              icon={<BsLayoutSidebarInset />}
              _hover={{ bg: iconButtonHoverBg }}
            />
          </Tooltip>
        </Flex>

        {showApiSelection && (
          <Box marginTop="7%" marginBottom="3%">
            <Text marginLeft="10%" fontWeight={600} color={apiSelectText}>Select API</Text>
            <Center>
              <Select
                w="80%"
                bg={apiSelectBg}
                value={apiAddress}
                onChange={handleApiChange}
                id="api-select"
              >
                <option value="https://chatsocapi.sullivanoncomp.com">Production</option>
                <option value="https://staging-chatsocapi.sullivanoncomp.com">Staging</option>
                {process.env.REACT_APP_CURRENT_ENV === 'local' && (
                  <option value="http://localhost:8000">Local</option>
                )}
              </Select>
            </Center>
          </Box>
        )}

        {showSearch && (
          <Center>
            <InputGroup marginTop="8%" marginBottom="4%" w="90%" size="sm">
              <InputLeftElement marginTop=".3%" pointerEvents='none'>
                {loadingSearch ? <Spinner color={spinnerColor} size="sm" /> : <BiSearch color={searchIconColor} />}
              </InputLeftElement>
              <Input
                value={searchText}
                onChange={handleSearchChange}
                bg={searchInputBg}
                borderRadius="20px"
                placeholder='Search prior conversations'
                id="search-box"
              />
            </InputGroup>
          </Center>
        )}

        {/* Render Folders */}
        {folders && sortFoldersByRecentMove(Object.values(folders)).map(folder => (
          folderGroups[folder.id] && (
            <FolderComponent
              key={folder.id}
              folder={folder}
              chats={folderGroups[folder.id]}
              allChats={allChats}
              currentChat={currentChat}
              folders={folders}
              onChatClick={createChatClickHandler}
              onUpdateTitle={updateFolderTitle!}
              onDeleteFolder={deleteFolder!}
              onMoveToFolder={handleMoveToFolder}
              onRemoveFromFolder={handleRemoveFromFolder}
              onCreateFolderAndMove={handleCreateFolderAndMove}
              isSelectionMode={isSelectionMode}
              selectedChats={selectedChats}
              onToggleSelection={toggleSelection}
            />
          )
        ))}

        {/* Render Orphan Chats */}
        {orphanChats.map((chatId) => (
          <ChatContextMenu
            key={chatId}
            chatId={chatId}
            folders={folders || {}}
            onMoveToFolder={handleMoveToFolder}
            onRemoveFromFolder={handleRemoveFromFolder}
            onCreateFolderAndMove={handleCreateFolderAndMove}
          >
            <Box>
              <ChatButton
                chatId={chatId}
                title={allChats[chatId]?.title || "Untitled"}
                isCurrentChat={currentChat === chatId}
                onClick={createChatClickHandler(chatId)}
                isSelectionMode={isSelectionMode}
                isSelected={selectedChats.has(chatId)}
                onToggleSelection={toggleSelection}
              />
            </Box>
          </ChatContextMenu>
        ))}

        {searchText.length === 0 && hasMoreChats && (
          <Center ref={observerTarget} minH="56px" py={4}>
            {loadingMoreChats && <Spinner color={spinnerColor} size="sm" />}
          </Center>
        )}
        {/* Bottom padding so content isn't hidden behind floating footer */}
        {Object.keys(allChats).length > 0 && <Box h="56px" />}
      </Box>

      {/* Floating footer — Delete all chats */}
      {Object.keys(allChats).length > 0 && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          px={3}
          py={2}
          bg={sidebarBg}
          borderTop="1px solid"
          borderColor="whiteAlpha.200"
          _before={{
            content: '""',
            position: "absolute",
            top: "-24px",
            left: 0,
            right: 0,
            height: "24px",
            background: `linear-gradient(to top, ${sidebarBg === '#17404e' ? '#17404e' : 'var(--chakra-colors-gray-900)'}, transparent)`,
            pointerEvents: "none",
          }}
        >
          <Button
            w="100%"
            size="sm"
            variant="ghost"
            color="red.300"
            fontWeight={500}
            fontSize="13px"
            leftIcon={<BiTrash size="15px" />}
            onClick={onDeleteAllOpen}
            _hover={{ bg: "whiteAlpha.100", color: "red.200" }}
          >
            Delete all chats
          </Button>
        </Box>
      )}

      {/* Delete All Chats Confirmation Modal */}
      <Modal isOpen={isDeleteAllOpen} onClose={handleDeleteAllModalClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="xl"
          bg={modalBg}
          boxShadow="2xl"
        >
          <ModalHeader
            fontSize="lg"
            fontWeight={700}
            color={modalHeaderColor}
            pb={1}
          >
            Delete All Chats
          </ModalHeader>
          <ModalCloseButton isDisabled={isDeleting} />
          <ModalBody>
            <Text mb={3} color={modalTextColor} fontSize="14px">
              This will permanently delete <strong>all {Object.keys(allChats).length} chat(s)</strong> and any associated folders. This action cannot be undone.
            </Text>
            <Text mb={2} color={modalSecondaryTextColor} fontSize="13px" fontWeight={500}>
              To confirm, type: <Text as="span" fontWeight={700} color={modalHeaderColor}>{CONFIRM_PHRASE}</Text>
            </Text>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type the confirmation phrase..."
              size="sm"
              borderRadius="md"
              isDisabled={isDeleting}
              _focus={{ borderColor: "red.400", boxShadow: "0 0 0 1px var(--chakra-colors-red-400)" }}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={handleDeleteAllModalClose}
              isDisabled={isDeleting}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteAllChats}
              isDisabled={deleteConfirmText !== CONFIRM_PHRASE || isDeleting}
              isLoading={isDeleting}
              loadingText="Deleting..."
              size="sm"
            >
              Delete All Chats
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default Sidebar;
